<script lang="ts" module>
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { Progress } from 'bits-ui';
	import { percent } from '$lib/utils/css';
	import { tv } from '../tv';
	import { MAIN_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const progressBarVariants = tv({
		base: 'flex overflow-hidden disabled-state',
		variants: {
			size: { '300': 'h-[0.5rem]', '400': 'h-[0.75rem]', '500': 'h-[0.875rem]' },
			variant: MAIN_COLOR_VARIANTS,
			fill: {
				Solid: 'border-(--v-main-line) bg-(--v-main) text-(--v-container)',
				Soft: 'border-(--v-container-line) bg-(--v-container) text-(--v-main)',
				None: 'border-(--v-main) bg-inherit text-(--v-main)'
			},
			radii: RADII_VARIANTS,
			outlined: { true: 'border-b300' }
		},
		defaultVariants: { variant: 'Secondary', size: '400', fill: 'Soft', radii: 'Pill' }
	});

	export interface ProgressBarProps
		extends Omit<HTMLAttributes<HTMLElement>, 'class'>, VariantProps<typeof progressBarVariants> {
		as?: string;
		class?: ClassValue;
		max?: number;
		min?: number;
		ref?: HTMLElement | null;
		value?: number;
	}
</script>

<script lang="ts">
	let {
		as = 'span',
		class: className,
		fill,
		max,
		min = 0,
		outlined,
		radii,
		ref = $bindable(null),
		size,
		value,
		variant,
		...rest
	}: ProgressBarProps = $props();

	const progress = $derived(
		value !== undefined && max !== undefined
			? { value, width: percent(min, max, value) }
			: undefined
	);
</script>

<Progress.Root {max} {min} value={progress?.value ?? null}>
	{#snippet child({ props })}
		<svelte:element
			this={as}
			bind:this={ref}
			{...props}
			class={[progressBarVariants({ fill, outlined, radii, size, variant }), className]}
			{...rest}
		>
			{#if progress}
				<span class="inline-block h-full bg-current" style:width="{progress.width}%"></span>
			{:else}
				<span class="animate-slide-packet inline-block h-full bg-current"></span>
			{/if}
		</svelte:element>
	{/snippet}
</Progress.Root>
